import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { useAuth } from '../../../hooks/useAuth';
import { PageMeta } from '../shared/PageMeta';
import { ApiRequestError } from '../../../api/client';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginForm = z.infer<typeof loginSchema>;

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    try {
      await login(data.email, data.password);
      toast.success('Welcome back!');
      navigate('/marketplace/dashboard');
    } catch (err) {
      const message = err instanceof ApiRequestError ? err.message : 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <PageMeta title="Log In" description="Log in to the Second Chance Seed Network" path="/marketplace/login" />
      <div className="max-w-md mx-auto">
        <div className="card p-8">
          <h2 className="text-2xl mb-2 text-center">Welcome Back</h2>
          <p className="text-spi-slate text-sm text-center mb-8">Log in to your marketplace account</p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="input-label">Email</label>
              <input {...register('email')} type="email" autoComplete="email" className="input" placeholder="you@example.com" />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label className="input-label">Password</label>
              <input {...register('password')} type="password" autoComplete="current-password" className="input" placeholder="Enter your password" />
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
            </div>

            <div className="flex justify-end">
              <Link to="/marketplace/forgot-password" className="text-sm text-spi-green hover:underline">
                Forgot password?
              </Link>
            </div>

            <button type="submit" disabled={loading} className="btn btn-primary w-full">
              {loading ? 'Logging in...' : 'Log In'}
            </button>
          </form>

          <p className="text-center text-sm text-spi-slate mt-6">
            Want to join?{' '}
            <Link to="/marketplace/apply" className="text-spi-green font-semibold hover:underline">
              Apply for an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
